/**
 * Content Filter Utility for Sportona
 * Client-side validation to prevent profanity, vulgarity, explicit language,
 * and adult/commercial sex/massage/girlfriend services.
 */

export const CONTENT_POLICY_ERROR = "This field contains disallowed or inappropriate language.";

const BLOCKED_TERMS = [
  "anal", "arsehole", "asshole", "asswipe", "bastard", "bc", "bdsm", "benchod",
  "bhenchod", "bfe", "bhosdike", "bhosdiwale", "bitch", "bitchass", "bitchy", "blowjob",
  "body rub", "bodyrub", "boobs", "boobies", "bordello", "brothel", "bullshit",
  "callboy", "callboys", "callgirl", "callgirls", "chutiya", "chutiye", "clit",
  "clitoris", "cock", "cockhead", "cocksucker", "cum", "cumshot", "cunt",
  "deepthroat", "dick", "dickhead", "dildo", "dipshit", "doggystyle", "dyke",
  "ejaculate", "erotic", "erotica", "erotik", "escort", "escorts", "escorting",
  "faggot", "fck", "fellatio", "fuck", "fuckboy", "fucker", "fucking", "fuckwit",
  "fuk", "fuq", "fvck", "gaandu", "gandu", "gangbang", "gawad", "gfe",
  "girlfriend", "girlfriend experience", "girlfriend service", "handjob",
  "happy ending", "happyending", "harami", "hentai", "hoe", "hooker", "hookers",
  "hookup", "hookups", "horny", "jackass", "jerkoff", "kahba", "khawal", "kos",
  "kosomak", "kuss", "kutti", "lauda", "lingam", "lodu", "lund", "madarchod",
  "manyoka", "manyook", "manyooc", "massage", "massages", "masseur", "masseuse",
  "massaging", "masturbate", "milf", "mofo", "motherfucker", "motherfucking",
  "neek", "neekh", "nigga", "nigger", "nik", "nsfw", "nude", "nudes", "nuru",
  "onlyfans", "orgasm", "orgy", "paedophile", "pedophile", "penis", "pennis",
  "pimp", "pimping", "porn", "pornhub", "porno", "prostitute", "prostitutes",
  "prostitution", "pussy", "randi", "rape", "rapist", "retard", "retarded",
  "rimjob", "rubdown", "semen", "sensual", "sex", "sexcam", "sexchat", "sexed",
  "sexist", "sext", "sexting", "sexual", "sexy", "sexx", "sharmoota", "sharmota",
  "shit", "shithead", "shitty", "shite", "sht", "slut", "slutty", "spaz",
  "sperm", "suar", "sugar baby", "sugar daddy", "sugar momma", "sugarbaby",
  "sugardaddy", "sugarmomma", "tantra", "tantric", "teez", "threesome", "tits",
  "titties", "titty", "tosser", "tramp", "twat", "vagina", "wank", "wanker",
  "whore", "whorehouse", "xvideos", "xxx", "yoni",
  // Arabic script
  "احا", "خول", "زب", "شرموطة", "طيز", "عرص", "قحبة", "قواد", "كس", "منيك", "منيوك", "منيوكة", "نيك"
];

const ALLOWED_WORDS = new Set([
  "adult", "adults", "arsenal", "assessment", "assessments", "assistant",
  "assistants", "association", "associations", "athlete", "athletes",
  "badminton", "bass", "class", "classes", "classic", "classical", "cockatoo",
  "compass", "dickson", "essex", "fitness", "glass", "grass", "mass",
  "paddle", "pass", "passage", "passed", "passes", "passing",
  "passion", "punch", "scunthorpe", "session", "sessions", "sussex", "title", "titles"
]);

/**
 * Maps leetspeak characters to standard letters
 */
function applyLeet(str) {
  return str
    .replace(/0/g, 'o')
    .replace(/[1!|]/g, 'i')
    .replace(/3/g, 'e')
    .replace(/[@4]/g, 'a')
    .replace(/[$5]/g, 's')
    .replace(/7/g, 't');
}

/**
 * Collapses consecutive repeating characters (e.g., "fuuuuck" -> "fuck")
 */
function collapseRepeats(str) {
  return str.replace(/(.)\1+/g, '$1');
}

const BLOCKED_SET = new Set();
for (const term of BLOCKED_TERMS) {
  const norm = term.toLowerCase().replace(/[^a-z0-9]/gi, '');
  if (norm) {
    BLOCKED_SET.add(norm);
    BLOCKED_SET.add(collapseRepeats(applyLeet(norm)));
  }
}

/**
 * Checks if a single word or token is clean
 */
function isTokenBlocked(token) {
  if (!token || token.length < 2) return false;
  if (ALLOWED_WORDS.has(token)) return false;

  const leet = applyLeet(token);
  const collapsed = collapseRepeats(leet);

  if (BLOCKED_SET.has(token) || BLOCKED_SET.has(leet) || BLOCKED_SET.has(collapsed)) {
    return true;
  }

  // Check common inflections (e.g. "fucking", "shits", "fucked", "massages")
  const suffixes = ['ings', 'ing', 'ers', 'ies', 'ied', 'ier', 'est', 'es', 'ed', 'er', 'ly', 's', 'y'];
  for (const suffix of suffixes) {
    if (collapsed.endsWith(suffix)) {
      const stem = collapsed.slice(0, -suffix.length);
      if (stem.length >= 3 && BLOCKED_SET.has(stem) && !ALLOWED_WORDS.has(stem)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks whether user input contains prohibited words or evasions.
 * Returns true if clean, false if inappropriate language or adult services are detected.
 * 
 * @param {string} text - User input string
 * @returns {boolean}
 */
export function isCleanContent(text) {
  if (!text || typeof text !== 'string') return true;
  const trimmed = text.trim();
  if (!trimmed) return true;

  // Split into tokens by non-alphanumeric boundaries
  const rawTokens = trimmed.toLowerCase().split(/[\s,.;:_/\\|*!~#&()[\]{}'"`+-]+/);

  for (const token of rawTokens) {
    if (isTokenBlocked(token)) {
      return false;
    }
  }

  // Check bigrams and trigrams for phrases (e.g. "happy ending", "sugar baby", "girlfriend experience")
  for (let i = 0; i < rawTokens.length - 1; i++) {
    const bigram = collapseRepeats(applyLeet(rawTokens[i] + rawTokens[i + 1]));
    if (BLOCKED_SET.has(bigram) && !ALLOWED_WORDS.has(bigram)) {
      return false;
    }
  }
  for (let i = 0; i < rawTokens.length - 2; i++) {
    const trigram = collapseRepeats(applyLeet(rawTokens[i] + rawTokens[i + 1] + rawTokens[i + 2]));
    if (BLOCKED_SET.has(trigram) && !ALLOWED_WORDS.has(trigram)) {
      return false;
    }
  }

  // Check for spaced letter evasion (e.g. "s e x", "f u c k")
  const singleLetters = rawTokens.filter(t => t.length === 1 && /[a-z]/i.test(t));
  if (singleLetters.length >= 3) {
    const joined = collapseRepeats(applyLeet(singleLetters.join('')));
    if (BLOCKED_SET.has(joined) && !ALLOWED_WORDS.has(joined)) {
      return false;
    }
  }

  // Check stripped string for continuous evasion
  const strippedOnlyLetters = trimmed.toLowerCase().replace(/[^a-z0-9]/gi, '');
  const collapsedOnlyLetters = collapseRepeats(applyLeet(strippedOnlyLetters));
  for (const blocked of BLOCKED_SET) {
    if (blocked.length >= 4 && collapsedOnlyLetters.includes(blocked) && !ALLOWED_WORDS.has(collapsedOnlyLetters)) {
      return false;
    }
  }

  return true;
}

/**
 * Returns error message if text violates policy, or null if clean.
 * 
 * @param {string} text
 * @returns {string|null}
 */
export function getContentPolicyError(text) {
  return isCleanContent(text) ? null : CONTENT_POLICY_ERROR;
}
